import { useEffect, useRef } from 'react';

const oldSteps = [
  { step: 1, label: 'Roof Cap에 볼트 체결', sub: '', highlight: false },
  { step: 2, label: '캡 뚜껑 닫기', sub: '', highlight: false },
  { step: 3, label: '실리콘 보조도구 준비', sub: '실리콘건, 헤라 등 준비', highlight: true },
  { step: 4, label: '실리콘 도포작업 (수작업)', sub: '숙련도에 따라 품질 편차 발생', highlight: true },
  { step: 5, label: '경화 대기', sub: '24시간 이상', highlight: true },
];

const newSteps = [
  { step: 1, label: 'Smart Cap에 볼트 체결', sub: '실리콘 패킹 자동 압착', isFinal: false },
  { step: 2, label: '캡 뚜껑 닫기 → 끝!', sub: '즉시 방수 완료!', isFinal: true },
];

const tableRows = [
  {
    feature: '시공 단계',
    icon: 'ri-list-check',
    smart: '2단계',
    existing: '5단계',
  },
  {
    feature: '보조도구',
    icon: 'ri-tools-fill',
    smart: '완전 불필요',
    existing: '반드시 필요',
  },
  {
    feature: '실리콘 작업',
    icon: 'ri-drop-fill',
    smart: '완전 불필요',
    existing: '반드시 필요',
  },
  {
    feature: '경화 대기',
    icon: 'ri-time-line',
    smart: '불필요, 즉시 방수',
    existing: '24시간 이상',
  },
  {
    feature: '경제성',
    icon: 'ri-coin-fill',
    smart: '개당 350원 절감',
    existing: '추가 비용 발생',
  },
  {
    feature: '방수 내구성',
    icon: 'ri-shield-star-fill',
    smart: '실리콘 패킹 내장',
    existing: '1~2년 후 균열·박리',
  },
  {
    feature: 'A/S 비용',
    icon: 'ri-customer-service-2-fill',
    smart: '없음',
    existing: '건당 15~40만원 반복',
  },
  {
    feature: '작업성',
    icon: 'ri-user-star-fill',
    smart: '누구나 균일 품질',
    existing: '숙련도에 의존',
  },
];

const ComparisonSection = () => {
  const titleRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    [titleRef, leftRef, rightRef, bannerRef, tableRef].forEach(
      (r) => r.current && observer.observe(r.current)
    );
    rowRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="comparison" className="py-24 bg-[#F8F9FC]">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">

        {/* 타이틀 */}
        <div
          ref={titleRef}
          className="text-center mb-14 opacity-0 translate-y-6 transition-all duration-700"
        >
          <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">Process Comparison</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1B2A4A] font-display">
            시공 프로세스 비교
          </h2>
          <p className="mt-4 text-gray-500 text-base max-w-xl mx-auto">
            단계 수가 곧 시간이고, 비용입니다
          </p>
          <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
        </div>

        {/* 좌우 스텝 비교 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* 왼쪽: 기존 Roof Cap */}
          <div
            ref={leftRef}
            className="opacity-0 translate-y-8 transition-all duration-700 delay-100 bg-white rounded-2xl border border-red-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-red-500 to-gray-600 px-6 py-5 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20">
                <i className="ri-tools-fill text-white text-lg" />
              </div>
              <div>
                <p className="text-white/70 text-xs font-medium tracking-widest uppercase">기존 방식</p>
                <h3 className="text-white font-bold text-lg leading-tight">기존 Roof Cap 방식</h3>
              </div>
              <span className="ml-auto bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">5단계</span>
            </div>
            <div className="px-6 py-6 space-y-0">
              {oldSteps.map((s, i) => (
                <div key={s.step} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${s.highlight ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {s.step}
                    </div>
                    {i < oldSteps.length - 1 && <div className="w-0.5 h-8 bg-gray-200 my-1" />}
                  </div>
                  <div className="pb-2 pt-1">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-gray-400">STEP {s.step}</span>
                    <p className={`font-semibold text-sm leading-snug mt-0.5 ${s.highlight ? 'text-red-600' : 'text-[#1B2A4A]'}`}>{s.label}</p>
                    {s.sub && (
                      <p className="text-red-400 text-xs mt-0.5 flex items-center gap-1">
                        <i className="ri-alert-fill text-xs" />{s.sub}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mx-6 mb-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <p className="text-red-600 text-sm font-semibold text-center leading-relaxed">
                총 5단계 &nbsp;·&nbsp; 실리콘 공정 필수 &nbsp;·&nbsp; 경화 대기 시간 발생
              </p>
            </div>
          </div>

          {/* 오른쪽: Smart Cap */}
          <div
            ref={rightRef}
            className="opacity-0 translate-y-8 transition-all duration-700 delay-200 bg-white rounded-2xl border border-[#1E6FD9]/20 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-[#1E6FD9] to-[#3B82F6] px-6 py-5 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20">
                <i className="ri-shield-check-fill text-white text-lg" />
              </div>
              <div>
                <p className="text-white/70 text-xs font-medium tracking-widest uppercase">스마트캡 방식</p>
                <h3 className="text-white font-bold text-lg leading-tight">Smart Cap 방식</h3>
              </div>
              <span className="ml-auto bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">2단계</span>
            </div>
            <div className="px-6 py-6 space-y-0">
              {newSteps.map((s, i) => (
                <div key={s.step} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${s.isFinal ? 'bg-[#FF6B35] text-white' : 'bg-[#1E6FD9] text-white'}`}>
                      {s.step}
                    </div>
                    {i < newSteps.length - 1 && <div className="w-0.5 h-8 bg-[#1E6FD9]/20 my-1" />}
                  </div>
                  <div className="pb-2 pt-1">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-gray-400">STEP {s.step}</span>
                    <p className="font-semibold text-sm text-[#1B2A4A] leading-snug mt-0.5">{s.label}</p>
                    {s.sub && (
                      <p className={`text-xs mt-0.5 flex items-center gap-1 ${s.isFinal ? 'text-[#FF6B35] font-bold' : 'text-[#1E6FD9]'}`}>
                        {s.isFinal
                          ? <><i className="ri-checkbox-circle-fill" />{s.sub}</>
                          : <><i className="ri-check-line" />{s.sub}</>
                        }
                      </p>
                    )}
                  </div>
                </div>
              ))}
              <div className="h-14" />
            </div>
            <div className="mx-6 mb-6 bg-[#EEF4FF] border border-[#1E6FD9]/30 rounded-xl px-4 py-3">
              <p className="text-[#1E6FD9] text-sm font-semibold text-center leading-relaxed">
                총 2단계 &nbsp;·&nbsp; 실리콘 공정 불필요 &nbsp;·&nbsp; 경화 대기 불필요
              </p>
            </div>
          </div>
        </div>

        {/* 강조 배너 */}
        <div
          ref={bannerRef}
          className="opacity-0 translate-y-6 transition-all duration-700 delay-300 mt-6"
        >
          <div className="relative bg-[#1B2A4A] rounded-2xl px-6 py-6 flex flex-col sm:flex-row items-center justify-center gap-4 overflow-hidden">
            <div className="absolute left-0 top-0 w-40 h-full bg-[#FF6B35]/10 rounded-2xl" />
            <div className="absolute right-0 top-0 w-40 h-full bg-[#1E6FD9]/10 rounded-2xl" />
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FF6B35]/20 flex-shrink-0 z-10">
              <i className="ri-flashlight-fill text-[#FF6B35] text-2xl" />
            </div>
            <p className="z-10 text-center sm:text-left">
              <span className="text-white font-bold text-xl lg:text-2xl">3단계 공정 삭제</span>
              <span className="text-gray-400 text-xl lg:text-2xl mx-3">=</span>
              <span className="text-[#FF6B35] font-bold text-xl lg:text-2xl">시공 속도 50% 향상</span>
            </p>
            <div className="z-10 hidden sm:flex items-center gap-1.5 bg-[#1E6FD9]/20 border border-[#1E6FD9]/30 px-4 py-2 rounded-full flex-shrink-0">
              <i className="ri-rocket-2-fill text-[#3B82F6] text-sm" />
              <span className="text-[#3B82F6] text-sm font-bold whitespace-nowrap">검증된 현장 데이터</span>
            </div>
          </div>
        </div>

        {/* 상세 비교 테이블 */}
        <div
          ref={tableRef}
          className="mt-16 opacity-0 translate-y-6 transition-all duration-700 delay-100"
        >
          <div className="text-center mb-8">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">Detail Comparison</span>
            <h3 className="mt-2 text-2xl lg:text-3xl font-bold text-[#1B2A4A] font-display">
              항목별 상세 비교
            </h3>
            <div className="mt-3 w-10 h-1 bg-[#FF6B35] mx-auto rounded-full" />
          </div>

          {/* 컬럼 헤더 */}
          <div className="grid grid-cols-3 mb-2 px-2">
            <div />
            <div className="text-center">
              <span className="relative inline-flex items-center gap-1.5 bg-[#1E6FD9] text-white text-xs font-bold px-4 py-1.5 rounded-full">
                <i className="ri-shield-check-fill" />
                Smart Cap
                <span className="ml-1 bg-white/25 text-white text-[10px] px-1.5 py-0.5 rounded-full">추천</span>
              </span>
            </div>
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 bg-gray-300 text-gray-600 text-xs font-semibold px-4 py-1.5 rounded-full">
                <i className="ri-contrast-2-fill" />
                기존 Roof Cap
              </span>
            </div>
          </div>

          {/* 테이블 본문 */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
            {tableRows.map((row, i) => (
              <div
                key={i}
                ref={(el) => { rowRefs.current[i] = el; }}
                className={`grid grid-cols-3 border-b border-gray-100 last:border-0
                  hover:bg-[#1E6FD9]/5 transition-colors duration-200
                  opacity-0 translate-y-3 transition-[opacity,transform] duration-500
                  ${i % 2 === 1 ? 'bg-[#FAFBFC]' : 'bg-white'}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                {/* 항목명 */}
                <div className="px-5 py-4 flex items-center gap-2.5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#1B2A4A]/5 flex-shrink-0">
                    <i className={`${row.icon} text-sm text-[#1B2A4A]`} />
                  </div>
                  <span className="font-semibold text-[#1B2A4A] text-sm leading-snug">{row.feature}</span>
                </div>

                {/* Smart Cap — 블루 하이라이트 */}
                <div className="px-4 py-4 flex items-center justify-center border-l border-[#1E6FD9]/10 bg-[#EEF4FF]">
                  <span className="inline-flex items-start gap-1.5 text-[#1B2A4A] font-semibold text-sm leading-snug">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#1E6FD9] flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-xs" />
                    </span>
                    {row.smart}
                  </span>
                </div>

                {/* 기존 Roof Cap */}
                <div className="px-4 py-4 flex items-center justify-center border-l border-gray-100">
                  <span className="inline-flex items-start gap-1.5 text-gray-500 text-sm leading-snug">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0 mt-0.5">
                      <i className="ri-close-line text-red-400 text-xs" />
                    </span>
                    {row.existing}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-400 text-xs mt-4">
            ※ 위 비교는 일반적인 현장 사용 조건 및 업계 평균 데이터를 기준으로 작성되었습니다.
          </p>
        </div>

      </div>
    </section>
  );
};

export default ComparisonSection;
